import React, { useEffect, useMemo, useState } from 'react'
import ComboBox from 'components/ComboBox/comboBox'
import FormRow, { FormRowProps } from 'components/FormRow'
import useWorkOrdersQuery, { WorkOrder } from '../../hooks/useWorkOrdersQuery'

export type WorkOrderPickerProps = {
	/** Selected work order ID */
	selectedWorkOrderId?: string,
	/** Callback when work order selection changes */
	onChange: ( workOrderId: string, workOrder?: WorkOrder ) => void,
	/** Form row props for validation and styling */
	formRowProps?: Partial<FormRowProps>,
	/** Whether the picker is disabled */
	isDisabled?: boolean,
	/** Placeholder text */
	placeholder?: string,
	/** Prepend element for the combo box */
	prepend?: string | React.ReactElement | ( string | React.ReactElement )[],
	/** Whether to show the "WO-" prefix in labels (default: true) */
	showPrefix?: boolean,
	/** Test ID for testing */
	'data-testid'?: string,
	/** Branch ID to filter work orders by */
	branchId?: string,
}

export default function WorkOrderPicker( {
	selectedWorkOrderId,
	onChange,
	formRowProps,
	isDisabled,
	placeholder = 'Search for work orders...',
	prepend,
	showPrefix = true,
	'data-testid': testId,
	branchId,
}: WorkOrderPickerProps ) {
	const { searchWorkOrders, workOrders, loading } = useWorkOrdersQuery()
	const [ searchString, setSearchString ] = useState( '' )

	// Held as state rather than searched straight from the callback so a branch
	// change re-scopes the current search — `ComboBox` only calls back on input.
	// The empty guard is the pre-conversion effect's: clearing must not search.
	useEffect( () => {
		if ( searchString ) {
			searchWorkOrders( searchString, branchId )
		}
	}, [ searchString, searchWorkOrders, branchId ] )

	// Fetch pre-selected work order when ID is provided without a prior search
	useEffect( () => {
		if ( selectedWorkOrderId ) {
			searchWorkOrders( selectedWorkOrderId, branchId )
		}
	}, [ selectedWorkOrderId, searchWorkOrders, branchId ] )

	const selectedWorkOrder = useMemo( () => (
		selectedWorkOrderId
			? workOrders.find( ( workOrder ) => workOrder.work_order_id.toString() === selectedWorkOrderId )
			: undefined
	), [ selectedWorkOrderId, workOrders ] )

	const handleChange = ( workOrder?: WorkOrder ) => {
		onChange( workOrder ? workOrder.work_order_id.toString() : '', workOrder )
	}

	return (
		<FormRow label="Work Order ID" {...formRowProps}>
			<ComboBox<WorkOrder>
				placeholder={placeholder}
				data={workOrders}
				getName={( workOrder ) => (
					showPrefix ? `WO-${workOrder.work_order_id}` : workOrder.work_order_id.toString()
				)}
				selectedOption={selectedWorkOrder}
				setSelectedOption={handleChange}
				setSearchString={setSearchString}
				debounceDelayMs={400}
				// The pre-conversion `EuiComboBox` wasn't async, so it filtered the current
				// results by the typed text while the next search was pending.
				async={false}
				isLoading={loading}
				isDisabled={isDisabled}
				isInvalid={formRowProps?.isInvalid}
				prepend={prepend}
				data-testid={testId}
			/>
		</FormRow>
	)
}
